import type { DynamicRouteData, SiteWixCodeSdkFactoryData, WixCodeApiFactoryArgs } from '@wix/thunderbolt-symbols'
import type { MetaTag, SiteLevelSeoData } from 'feature-seo'
import type { SeoWixCodeSdkFactoryData, SeoWixCodeSdkHandlers, SeoWixCodeSdkWixCodeApi, Link } from '..'
import { namespace } from '..'
import { initState } from './utils/generate-state'
import { mediaItemUtils } from '@wix/santa-platform-utils'
import { extractDynamicRouteData } from './utils/extract-dynamic-route-data'
import { resolveMetaTags } from './utils/resolve-meta-tags'

/**
 * Mirrors advanced-seo-utils `getUniqueLinks`: dedupe by `rel` keeping the last
 * occurrence (unless the link carries `hreflang`), dropping links missing rel/href.
 * Keeps the local `links` getter consistent with the last-wins result that is rendered.
 */
const getUniqueLinks = (links: Array<Link>): Array<Link> => {
	const seenRels = new Set<string>()
	return [...links]
		.reverse()
		.filter((link) => {
			if (!link || !link.rel || !link.href) {
				return false
			}
			const duplicate = seenRels.has(link.rel)
			seenRels.add(link.rel)
			return !duplicate || Boolean((link as { hreflang?: string }).hreflang)
		})
		.reverse()
}

/**
 * SEO SDK Factory
 * API Docs: https://www.wix.com/velo/reference/wix-seo.html
 */

export function SeoSdkFactory({
	featureConfig: pageLevelSeoData,
	handlers,
	platformEnvData,
	onPageWillUnmount,
}: WixCodeApiFactoryArgs<SiteWixCodeSdkFactoryData, SeoWixCodeSdkFactoryData, SeoWixCodeSdkHandlers>): {
	[namespace]: SeoWixCodeSdkWixCodeApi
} {
	const {
		setTitle,
		setLinks,
		addLinks,
		setMetaTags,
		addMetaTags: addMetaTagsHandler,
		setStatusCode,
		setStructuredData,
		renderSEOTags,
		resetSEOTags,
		onTPAOverrideChanged,
	} = handlers.seo
	const siteLevelSeoData = platformEnvData.seo as SiteLevelSeoData
	const dynamicRoutePayload = platformEnvData.router.dynamicRouteData as DynamicRouteData
	const { dynamicPageData, veloOverrides } = extractDynamicRouteData(dynamicRoutePayload, mediaItemUtils) || {}
	const { state, setVeloState, setState } = initState({
		siteLevelSeoData,
		pageLevelSeoData,
		veloOverrides,
		dynamicPageData,
	})
	let addMetaTagsQueue = Promise.resolve()

	if (process.env.browser) {
		onTPAOverrideChanged((tpaOverrides) => {
			state.tpaOverrides = tpaOverrides
		}).then(onPageWillUnmount)
	}

	return {
		[namespace]: {
			get title() {
				return state.velo.title
			},
			get links() {
				return state.velo.links
			},
			get metaTags() {
				return state.velo.metaTags
			},
			get structuredData() {
				return state.velo.structuredData
			},
			// TODO: remove
			get seoStatusCode() {
				return state.velo.statusCode
			},
			get statusCode() {
				return state.velo.statusCode
			},
			isInSEO() {
				return siteLevelSeoData.isInSEO
			},
			async setTitle(title: string) {
				setTitle(title)
				setVeloState({ title })
			},
			async setLinks(links: Array<Link>) {
				setLinks(links)
				setVeloState({ links })
			},
			async addLinks(links: Array<Link>) {
				const normalizedLinks = Array.isArray(links) ? links : []
				await addLinks(normalizedLinks)
				setVeloState({ links: getUniqueLinks([...state.velo.links, ...normalizedLinks]) })
			},
			async setMetaTags(metaTags: Array<MetaTag>) {
				const resolvedMetaTags = resolveMetaTags(metaTags, mediaItemUtils) as Array<MetaTag>
				setMetaTags(resolvedMetaTags)
				setVeloState({ metaTags: resolvedMetaTags })
			},
			async addMetaTags(metaTags: Array<MetaTag>) {
				const normalizedMetaTags = Array.isArray(metaTags)
					? metaTags.filter((metaTag) => metaTag && typeof metaTag === 'object' && !Array.isArray(metaTag))
					: []
				const resolvedMetaTags = resolveMetaTags(normalizedMetaTags, mediaItemUtils) as Array<MetaTag>
				const addMetaTagsTask = addMetaTagsQueue.then(async () => {
					await addMetaTagsHandler(resolvedMetaTags)
					const api = (await import('@wix/advanced-seo-utils/renderer' /* webpackChunkName: "seo-api" */)) as {
						addMetaTag(data: any, props: MetaTag): any
						getMetaTags(data: any): Array<MetaTag>
					}
					const currentTags = state.velo.metaTags.reduce((tags, metaTag) => api.addMetaTag(tags, metaTag), {
						tags: [],
					})
					const tags = resolvedMetaTags.reduce((data, metaTag) => api.addMetaTag(data, metaTag), currentTags)
					setVeloState({ metaTags: api.getMetaTags(tags) })
				})
				addMetaTagsQueue = addMetaTagsTask.catch(() => undefined)
				await addMetaTagsTask
			},
			async setStructuredData(structuredData: Array<Record<string, any>>) {
				setStructuredData(structuredData)
				setVeloState({ structuredData })
			},
			// TODO: remove
			async setSeoStatusCode(seoStatusCode: number) {
				setStatusCode(seoStatusCode)
				setVeloState({ statusCode: seoStatusCode })
			},
			setStatusCode(statusCode: number) {
				setStatusCode(statusCode)
				setVeloState({ statusCode })
			},
			async renderSEOTags(payload) {
				const { isComponentItemType } = await import(
					'@wix/advanced-seo-utils/renderer' /* webpackChunkName: "seo-api" */
				)

				await renderSEOTags(payload)
				await setState({
					veloState: state.velo,
					...(isComponentItemType(payload?.itemType)
						? {
								componentsItemPayload: [...state.componentsItemPayload, payload],
							}
						: {
								veloItemPayload: payload,
							}),
				})
			},
			async resetSEOTags() {
				resetSEOTags()
				await setState({
					veloState: state.userOverrides,
					componentsItemPayload: [],
				})
			},
		},
	}
}
